import { createEditingStyle } from 'ol/style/Style';
import GeoJSON from 'ol/format/GeoJSON';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useApplicationState from '../hooks/useApplicationState';
import { useOpenLayers, VectorLayer, VectorSource } from '../react-ol';
import createGeometryEvent from '../utils/createGeometryEvent';
import useGeometrySelect from '../hooks/useGeometrySelect';
import useGeometryStyles from '../hooks/useGeometryStyles';
import getMeasurementStyles from '../utils/getMeasurementStyles';
import { getLabelStyle } from '../hooks/useAddCustomLabel/fragments';
import { useGeometryArea } from '../hooks/useGeometryArea';
export var Geometry = _ref => {
  var {
    data,
    id,
    label,
    allowMapMarker,
    canDraw,
    canModify,
    canSelect,
    canSnap,
    canToggle,
    defaultVisible,
    drawShape,
    layerMarkerType,
    markerCoordinates,
    markerStyle,
    maxZoom,
    minZoom,
    onChange,
    onClick,
    onDrawEnd,
    onDrawStart,
    onHighlight,
    onModifyEnd,
    onModifyStart,
    onPostrender,
    onSelect,
    onUnhighlight,
    onUploadedGeometriesChange,
    showAreaMeasurements = false,
    showLineMeasurements = false,
    showVertices = false,
    style,
    zIndex,
    multiSelect,
    reference,
    boundaryType
  } = _ref;
  var {
    map,
    featureCollection,
    layerRef
  } = useOpenLayers();
  var {
    highlightedFeatureRef,
    selectedFeatureRef
  } = useApplicationState();
  /**
   * highlightedFeatureRef is shared between Geometry instances, so
   * keep a record of whether we have a feature highlighted in this
   * Geometry, otherwise the mousemove event will end up remove the
   * highlight from features which don't belong to us.
   */

  var ourHighlightedFeatureRef = useRef(null);
  var {
    measurementAreaStyle,
    measurementBaseStyles,
    renderStyle,
    renderStyles,
    selectStyles,
    renderVerticesStyle,
    measurementLineStyle,
    highlightStyles,
    drawStyle
  } = useGeometryStyles({
    showVertices,
    style
  });
  var {
    getGeometryAreaWithSquareMetre
  } = useGeometryArea();
  /**
   * `getStyleRef` contains a reference to the style function.
   *
   * OpenLayers isn't a React component, so if we used getCallback
   * here to create a new getStyles function whenever the dependencies
   * changed, we'd have to then assign that function to all the
   * features which currently had the old getStyles function
   * assigned to them.
   *
   * So instead, we create a reference to a function. The
   * function is recreated and reassigned to the reference whenever
   * the dependencies change. The getStyles function then calls
   * whatever funciton the reference is currently pointing at.
   *
   * This means that when a feature calls getStyles it will always
   * end up calling the most current version of the function.
   */

  var getStylesRef = useRef();
  var getStyles = useCallback((feature, resolution) => {
    var _getStylesRef$current;

    return (_getStylesRef$current = getStylesRef.current) === null || _getStylesRef$current === void 0 ? void 0 : _getStylesRef$current.call(getStylesRef, feature, resolution);
  }, []);
  useEffect(() => {
    var _layerRef$current;

    var returnMeasurementStyles = (showLineMeasurements || showAreaMeasurements) && measurementBaseStyles && measurementAreaStyle && measurementLineStyle;

    getStylesRef.current = feature => {
      if (returnMeasurementStyles) {
        var geometry = feature.getGeometry();
        return getMeasurementStyles({
          areaStyle: measurementAreaStyle,
          lineStyle: measurementLineStyle,
          showAreaMeasurements,
          showLineMeasurements,
          shapeStyle: measurementBaseStyles,
          geometry,
          getArea: geom => getGeometryAreaWithSquareMetre(geom)
        });
      } else if (renderStyles) {
        var _updatedStyle$getText2;

        var featureName = feature.get('name');

        if (selectedFeatureRef.current === feature) {
          var _updatedStyle$getText;

          var _updatedStyle = selectStyles === null || selectStyles === void 0 ? void 0 : selectStyles[0].clone();

          _updatedStyle === null || _updatedStyle === void 0 ? void 0 : (_updatedStyle$getText = _updatedStyle.getText()) === null || _updatedStyle$getText === void 0 ? void 0 : _updatedStyle$getText.setText(featureName);
          return featureName ? [...(selectStyles !== null && selectStyles !== void 0 ? selectStyles : []), _updatedStyle] : selectStyles;
        }

        if (ourHighlightedFeatureRef.current === feature) {
          var _updatedStyle2$getTex;

          var tempStyle = highlightStyles || renderStyles;

          var _updatedStyle2 = tempStyle === null || tempStyle === void 0 ? void 0 : tempStyle[0].clone();

          (_updatedStyle2$getTex = _updatedStyle2.getText()) === null || _updatedStyle2$getTex === void 0 ? void 0 : _updatedStyle2$getTex.setText(featureName);
          return featureName ? [...tempStyle, _updatedStyle2] : tempStyle;
        }

        var updatedStyle = renderStyles === null || renderStyles === void 0 ? void 0 : renderStyles[0].clone();
        updatedStyle === null || updatedStyle === void 0 ? void 0 : (_updatedStyle$getText2 = updatedStyle.getText()) === null || _updatedStyle$getText2 === void 0 ? void 0 : _updatedStyle$getText2.setText(featureName);
        return featureName ? [...renderStyles, updatedStyle] : renderStyles;
      } else {
        return createEditingStyle();
      }
    }; // since our getStyles function has just changed, make sure
    // any styled features get redrawn.


    layerRef === null || layerRef === void 0 ? void 0 : (_layerRef$current = layerRef.current) === null || _layerRef$current === void 0 ? void 0 : _layerRef$current.changed();
  }, [getGeometryAreaWithSquareMetre, highlightStyles, layerRef, measurementAreaStyle, measurementBaseStyles, measurementLineStyle, renderStyles, selectStyles, selectedFeatureRef, showAreaMeasurements, showLineMeasurements]);
  useGeometrySelect({
    onSelect,
    multiSelect,
    canSelect,
    selectStyles,
    interactionId: "".concat(id, "-interaction"),
    layerId: id
  });
  var styleLabelPointFeature = useCallback(feature => {
    var style = getLabelStyle(feature);
    feature.setStyle(style);
  }, []);
  var featureList = useMemo(() => {
    if (!data) return;

    try {
      return new GeoJSON().readFeatures(data).map(f => {
        if (f.get('textLabel')) {
          styleLabelPointFeature(f);
        }

        return f;
      });
    } catch (e) {
      console.error(e);
      return;
    }
  }, [data, styleLabelPointFeature]);
  useEffect(() => {
    if (!data) return;
    featureList === null || featureList === void 0 ? void 0 : featureList.forEach(feature => {
      return featureCollection === null || featureCollection === void 0 ? void 0 : featureCollection.current.push(feature);
    });
  }, [data, featureCollection, featureList]);
  useEffect(() => {
    if (!map || !onClick) return;

    var handleOnClick = () => {
      map.on('click', e => {
        return e.target.forEachFeatureAtPixel(e.pixel, feature => {
          if (feature) {
            onClick(createGeometryEvent(feature, e));
          }
        });
      });
    };

    map.on('click', handleOnClick);
    return () => {
      map.un('click', handleOnClick);
    };
  }, [map, onClick]); // TODO handle multiple selections/highlights

  var modifyingRef = useRef(false);

  function handleModifyStart(args) {
    modifyingRef.current = true;
    onModifyStart === null || onModifyStart === void 0 ? void 0 : onModifyStart(args);
  }

  function handleModifyEnd(args) {
    modifyingRef.current = false;
    onModifyEnd === null || onModifyEnd === void 0 ? void 0 : onModifyEnd(args);
  }

  useEffect(() => {
    if (!map) return;
    var currentLayer = layerRef === null || layerRef === void 0 ? void 0 : layerRef.current;

    var onPointerMove = e => {
      var _features$0$getGeomet;

      // only highlight things in modify mode, when not currently modifying
      if (modifyingRef.current) return;
      var features = map.getFeaturesAtPixel(e.pixel);

      if (features.length === 0 && ourHighlightedFeatureRef.current) {
        highlightedFeatureRef.current = null;
        ourHighlightedFeatureRef.current = null;
        onUnhighlight === null || onUnhighlight === void 0 ? void 0 : onUnhighlight(e);
        currentLayer === null || currentLayer === void 0 ? void 0 : currentLayer.changed();
      } else if (features.length > 0 && ((_features$0$getGeomet = features[0].getGeometry()) === null || _features$0$getGeomet === void 0 ? void 0 : _features$0$getGeomet.getType()) === 'Polygon') {
        if (ourHighlightedFeatureRef.current !== features[0]) {
          highlightedFeatureRef.current = features[0];
          ourHighlightedFeatureRef.current = features[0];
          onHighlight === null || onHighlight === void 0 ? void 0 : onHighlight(e);
          currentLayer === null || currentLayer === void 0 ? void 0 : currentLayer.changed();
        }
      }
    };

    map.on('pointermove', onPointerMove);
    return () => {
      map.un('pointermove', onPointerMove);
    };
  }, [highlightedFeatureRef, layerRef, map, onHighlight, onUnhighlight]);

  if (!renderStyle) {
    console.error('No render style set for geometry.');
    return null;
  }

  if (showVertices && !renderVerticesStyle) {
    console.error('No vertices style set for geometry.');
    return null;
  }

  if (!featureList) {
    return null;
  }

  return /*#__PURE__*/React.createElement(VectorLayer, {
    id: id,
    label: label,
    reference: reference,
    canToggle: canToggle,
    canSelect: canSelect,
    zIndex: zIndex,
    visible: defaultVisible,
    style: getStyles,
    declutter: true
    /* openlayers checks to render if !(zoom <= minZoom || zoom > maxZoom),
     * but if zoom === 4  && .minZoomObj === 4 we want the  toObj be visible
     * so we -1 from min zoom before passing it to openlayers */
    ,
    minZoom: minZoom ? minZoom === 0 ? 0 : minZoom - 1 : undefined,
    maxZoom: maxZoom,
    onPostrender: onPostrender
  }, /*#__PURE__*/React.createElement(VectorSource, {
    features: featureList,
    canDraw: canDraw,
    drawShape: drawShape,
    canModify: canModify,
    onChange: onChange,
    onDrawEnd: onDrawEnd,
    onDrawStart: onDrawStart,
    onModifyEnd: handleModifyEnd,
    onModifyStart: handleModifyStart,
    onUploadedGeometriesChange: onUploadedGeometriesChange,
    drawStyle: drawStyle,
    allowMapMarker: allowMapMarker,
    layerMarkerType: layerMarkerType,
    markerStyle: markerStyle,
    markerCoordinates: markerCoordinates,
    measurementBaseStyles: measurementBaseStyles,
    measurementAreaStyle: measurementAreaStyle,
    measurementLineStyle: measurementLineStyle,
    showLineMeasurements: showLineMeasurements,
    showAreaMeasurements: showAreaMeasurements,
    canSnap: canSnap,
    layerId: id,
    label: label,
    boundaryType: boundaryType
  }));
};